import * as yup from 'yup';
import { Trans, useTranslation } from 'react-i18next';
import { TypedDocumentNode } from '@apollo/client';
import { useForm } from 'react-form-state-manager';

import { GetTicketQuery as GetTicket } from '__generated__/graphql';

import { TranslatableTextArea } from '../../../Translations/Inputs';
import { dateRangeObject } from '../../../Common/DateRangeForm';
import { getServerErrors } from '../../../../common/helpers';
import { translationsForAttribute } from '../../../Translations/helpers';
import GetEventLayoutQuery from '../GetEventLayoutQuery';
import PricingForm from '../../../Common/PricingForm';
import UI from '../../../../common/UI';
import useEditTicketMutation from './useEditTicketMutation';
import useNavigationPrompt from '../../../../common/useNavigationPrompt';
import useNumberInput from '../../../../common/useNumberInput';
import useProject from '../../../useProject';
import useValidation from '../../../../common/useValidation';

type Event = GetTicket['event'];
type Ticket = GetTicket['ticket'];

const editTicketPricingSchema = yup.object().shape({
  vat_percentage: yup.number().min(0).max(99.999).nullable(),
  promotions: yup.array(yup.object().shape({
    title: yup.string().nullable().test('required', '', (value, { path }) => (
      path === 'promotions[0].title' ? true : !!value
    )),
    capacity: yup.number().nullable(),
    description: yup.string().ensure(),
    amount: yup.number().nullable(),
    ...dateRangeObject(),
  })),
});

export interface EditTicketPricingFormProps {
  ticket: Ticket;
  event: Event
  refetchQueries?: TypedDocumentNode[];
}

const EditTicketPricingForm = ({
  ticket, event, refetchQueries = [GetEventLayoutQuery],
}: EditTicketPricingFormProps) => {
  const { t } = useTranslation();
  const project = useProject();
  const numberInput = useNumberInput();

  const getValues = (ticket: Ticket) => ({
    promotions: ticket.promotions.map((promotion) => ({
      id: promotion.id,
      title: promotion.title,
      title_translations: translationsForAttribute(promotion.translations, 'title'),
      capacity: promotion.capacity,
      amount: promotion.amount,
      start_date: promotion.start_date,
      end_date: promotion.end_date,
      active: promotion.active,
    })),
    vat_percentage: ticket.vat_percentage > 0 ? ticket.vat_percentage : null,
    hide_on_invoice: ticket.hide_on_invoice,
    invoice_text: ticket.invoice_text,
    invoice_text_translations: translationsForAttribute(ticket.translations, 'invoice_text'),
    ledger_account: ticket.ledger_account,
    allow_companies: ticket.allow_companies,
  });

  const form = useForm({
    values: getValues(ticket),
  });

  useNavigationPrompt(form.changed());

  const [editTicket, { error, loading }] = useEditTicketMutation({
    input: {
      id: ticket.id,
      pricing: {
        ...form.values,
        promotions: form.values.promotions.length === 1
          // Since the promotion capacity field disappears when there is only 1 promotion,
          // make sure we unset it on save.
          ? [{ ...form.values.promotions[0], title: null, capacity: null, active: true }]
          : form.values.promotions,
      },
    },
    onSuccess: (ticket) => {
      form.reset(getValues(ticket));
    },
    refetchQueries,
  });

  const { valid, errors } = useValidation({
    schema: editTicketPricingSchema,
    values: form.values,
    externalErrors: getServerErrors(error, 'pricing'),
  });

  const hasVatNumber = Boolean(event.vat_number || project.organisation_vat_number);

  return (
    <UI.Form onSubmit={() => editTicket()}>
      <UI.FormGrid>
        <UI.ServerErrors error={error} />

        <UI.Div>
          <UI.Legend sc={{ mb: 1 }}>
            {t('pricing')}
          </UI.Legend>
          <UI.InputDescription>
            {t('ticket_pricing_description')}
          </UI.InputDescription>
        </UI.Div>

        <UI.Div>
          <PricingForm form={form} promotions={ticket.promotions} locale={ticket.locale} isTicket errors={errors} />
        </UI.Div>

        <UI.HR />

        <UI.Div>
          <UI.Legend sc={{ mb: 1 }}>
            {t('invoicing')}
          </UI.Legend>
        </UI.Div>

        <UI.Div>
          <UI.InputGroup>
            <UI.InputLabel>{t('vat_rate')}</UI.InputLabel>
            <UI.InputDescription>
              {hasVatNumber && t('vat_description')}
              {!hasVatNumber && (
                <Trans i18nKey="charging_vat_description">
                  <UI.Link to={`/${project.id}/events/${event.id}/settings/payments`} />
                </Trans>
              )}
            </UI.InputDescription>
            {hasVatNumber && (
              <UI.FormGrid sc={{ columns: ['1fr', '1fr 1fr'] }}>
                <UI.InputWrapper>
                  <UI.Input
                    {...form.text(
                      'vat_percentage',
                      { inputHandler: numberInput({ min: 0, max: 99.999, decimals: 3 }) },
                    )}
                    sc={{ pr: 4 }}
                  />
                  <span>%</span>
                </UI.InputWrapper>
                <UI.ErrorMessages attribute={t('vat_rate')} errors={errors.vat_percentage} />
              </UI.FormGrid>
            )}
          </UI.InputGroup>
        </UI.Div>

        <UI.InputGroup sc={{ invalid: form.touched.hide_on_invoice && !!errors.hide_on_invoice }}>
          <UI.InputLabel>{t('hide_on_invoice')}</UI.InputLabel>
          <UI.InputDescription>
            {t('hide_on_invoice_ticket_description')}
          </UI.InputDescription>
          <UI.Checkbox {...form.checkbox('hide_on_invoice')}>
            {t('hide_on_invoice')}
          </UI.Checkbox>
          <UI.ErrorMessages attribute={t('hide_on_invoice')} errors={errors.hide_on_invoice} />
        </UI.InputGroup>

        {!form.values.hide_on_invoice && (
          <UI.FormGrid>
            <UI.InputGroup sc={{ valid: !errors.invoice_text, touched: form.touched.invoice_text }}>
              <UI.InputLabel htmlFor="InvoiceText">
                {t('invoice_text')}
              </UI.InputLabel>
              <UI.InputDescription>
                {t('invoice_text_description')}
              </UI.InputDescription>
              <TranslatableTextArea
                form={form}
                errors={errors}
                name="invoice_text"
                title={t('invoice_text')}
                defaultLocale={event.locale}
                id="InvoiceText"
              />
              <UI.ErrorMessages attribute={t('invoice_text')} errors={errors.invoice_text} />
            </UI.InputGroup>
          </UI.FormGrid>
        )}

        {!ticket.business && !ticket.preregistration && (
          <UI.InputGroup sc={{ invalid: form.touched.allow_companies && !!errors.allow_companies }}>
            <UI.InputLabel>{t('allow_companies')}</UI.InputLabel>
            <UI.InputDescription>
              {t('allow_companies_description')}
            </UI.InputDescription>
            <UI.Checkbox {...form.checkbox('allow_companies')} disabled={ticket.business}>
              {t('allow_companies')}
            </UI.Checkbox>
            <UI.ErrorMessages attribute={t('allow_companies')} errors={errors.allow_companies} />
          </UI.InputGroup>
        )}

        <UI.HR />

        <UI.Div>
          <UI.Legend sc={{ mb: 1 }}>
            {t('ledger_account')}
          </UI.Legend>
          <UI.InputDescription>
            {t('ledger_account_description')}
          </UI.InputDescription>
        </UI.Div>

        <UI.FormGrid sc={{ columns: ['1fr', '1fr 1fr'] }}>
          <UI.InputGroup sc={{ valid: !errors.ledger_account, touched: form.touched.ledger_account }}>
            <UI.InputLabel htmlFor="ledger_account">
              {t('ledger_account')}
            </UI.InputLabel>
            <UI.DebouncedInput {...form.text('ledger_account')} id="ledger_account" />
            <UI.ErrorMessages attribute={t('ledger_account')} errors={errors.title} />
          </UI.InputGroup>
        </UI.FormGrid>

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={t('save_ticket')}
          closeTo={`/${project.id}/events/${event.id}/tickets`}
          loading={loading}
          valid={valid}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

export default EditTicketPricingForm;
